<template>
  <FilterOverview title="Flugbuchungen Controlling" :showFilterInitially="false" @reload="onReload">
    <template #toolbar-left>
      <div class="d-flex" style="align-items: center; gap: 12px">
        <b-button-group size="sm" class="d-flex" style="gap: 2px">
          <b-button
            v-for="priority in priorities"
            :key="priority.text"
            :id="priority.text"
            @click="onPriorityClick"
            :variant="
              prioritiesSelected.includes(priority.text)
                ? 'primary'
                : prioritiesSelected.length === 0 && priority.text === 'Alle'
                ? 'primary'
                : ''
            "
          >
            {{ priority.text }}
          </b-button>
        </b-button-group>
        <b-form-checkbox
          v-model="erledigteVorgaengeAusblenden"
          @change="onErledigteVorgaengeAusblendenClick"
          switch
        >
          Erledigte Vorgänge ausblenden
        </b-form-checkbox>
        <b-form-checkbox v-model="akzeptierteVorgaengeAusblenden" @change="resetFilters" switch>
          Akzeptierte Vorgänge ohne Aufgaben ausblenden
        </b-form-checkbox>

        <!-- Filter Reset Button -->
        <div
          v-if="isFilterSupplied"
          class="icon-button d-flex justify-content-center text-danger mr-4"
          style="width: 36px; min-width: 36px"
          @click="resetAllFilters"
          v-b-tooltip.hover.bottom
          title="Filter zurücksetzen"
        >
          <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
        </div>
      </div>
    </template>
    <template #table="{ tableHeight }">
      <AgGridVue
        lang="de"
        :style="{ height: tableHeight + 87 + 'px' }"
        class="ag-theme-alpine m-0 p-0"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowModelType="'serverSide'"
        :statusBar="statusBar"
        :headerHeight="50"
        suppressCellFocus
        suppressRowClickSelection
        rowHeight="50px"
        :getRowHeight="getRowHeight"
        :detailCellRenderer="'DetailCellRendererVorgaenge'"
        :detailRowAutoHeight="'true'"
        :masterDetail="true"
        :tooltipShowDelay="0"
        :enableCellTextSelection="true"
        suppressContextMenu
        @cellClicked="selectRowOnCellClicked"
        @rowClicked="onRowClicked"
        @rowSelected="onRowSelected"
        @grid-ready="onGridReady"
        :rowSelection="'multiple'"
      ></AgGridVue>
      <AufgabeErstellenModal
        :prefixedVerlinkungen="prefixedVerlinkungen"
        :disableAttachments="false"
        :fromFVC="false"
        :enableManualTask="true"
      />
    </template>
    <template #toolbar-right>
      <div
        class="icon-button d-inline-flex justify-content-center align-middle"
        style="width: 36px"
        @click="openAufgabenModalWithData"
        v-if="allVorgaengeWithSameReisetermin"
        v-b-tooltip.hover.bottom
        :title="'Aufgabe erstellen'"
      >
        <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
      </div>
    </template>
  </FilterOverview>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import FilterOverview from '@/components/common/filter-overview.vue';
import * as odataService from '@/core/common/services/odata.service';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import { format, add, parseISO, isAfter } from 'date-fns';
import CellRendererAufgaben from '@/components/produkte/reisetermine/cell-renderer-aufgaben.vue';
import CellRendererAktionenFlugbuchungen from '@/components/produkte/reisetermine/cell-renderer-aktionen-flugbuchungen.vue';
import CellRendererBnrMitGrWunsch from '@/components/produkte/reisetermine/cell-renderer-bnr-mit-gr-wunsch.vue';
import CellRendererTicketBot from '@/components/produkte/reisetermine/cell-renderer-ticket-bot.vue';
import AgDateRangePicker from '@/components/flugverfuegbarkeit/ag-date-range-picker.vue';
import { mapState, mapGetters } from 'vuex';
import CustomInfoIconHeader from '@/components/common/custom-info-icon-header.vue';
import CustomHeader from '@/components/flugverfuegbarkeit/custom-header.vue';
import DetailCellRendererVorgaenge from '@/components/vorgaenge/detail-cell-renderer-vorgaenge.vue';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import CellRendererAkzeptiertFlugbuchungen from '../../../components/common/cell-renderer-akzeptiert-flugbuchungen.vue';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import apiService from '@/core/common/services/api.service';
import BeschreibungTooltip from '@/components/aufgaben/beschreibung-tooltip.vue';
import { FunnelX, Bot, ListChecks } from 'lucide-vue';

export default {
  components: {
    ListChecks,
    AgGridVue,
    FilterOverview,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererAufgaben,
    // eslint-disable-next-line vue/no-unused-components
    StatusBarComponent,
    // eslint-disable-next-line vue/no-unused-components
    AgDateRangePicker,
    // eslint-disable-next-line vue/no-unused-components
    CustomInfoIconHeader,
    // eslint-disable-next-line vue/no-unused-components
    agColumnHeader: CustomHeader,
    // eslint-disable-next-line vue/no-unused-components
    DetailCellRendererVorgaenge,
    // eslint-disable-next-line vue/no-unused-components
    CustomTooltip,
    CellRendererAkzeptiertFlugbuchungen,
    CellRendererAktionenFlugbuchungen,
    CellRendererBnrMitGrWunsch,
    CellRendererTicketBot,
    AufgabeErstellenModal,
    // eslint-disable-next-line vue/no-unused-components
    BeschreibungTooltip,
    FunnelX,
  },
  data() {
    return {
      priorities: [
        { value: null, text: 'Alle' },
        { value: 0, text: 'A' },
        { value: 1, text: 'B' },
        { value: 2, text: 'C' },
        { value: 3, text: 'D' },
      ],
      prioritiesSelected: [],
      statusPruefungConverter: {
        AllGood: '<i class="fas fa-plane text-success fa-lg"></i>',
        SomethingWrong: '<i class="fas fa-plane text-warning fa-lg"></i>',
        NoSegment: '<i class="fas fa-plane text-danger fa-lg"></i>',
      },
      statusTicketsConverter: {
        AllGood: '<i class="fas fa-ticket-alt text-success fa-lg"></i>',
        CheckRequired: '<i class="fas fa-ticket-alt text-warning fa-lg"></i>',
        Critical: '<i class="fas fa-ticket-alt text-danger fa-lg"></i>',
      },
      gridApi: null,
      gridColumnApi: null,
      isFilterSupplied: false,
      erledigteVorgaengeAusblenden: true,
      akzeptierteVorgaengeAusblenden: true,
      prefixedVerlinkungen: [],
      allSelectedRows: [],
      columnDefs: null,
      statusBar: {
        statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
      },
      defaultColDef: {
        sortable: false,
        resizable: false,
        floatingFilter: true,
        suppressMenu: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          defaultToNothingSelected: true,
        },
        tooltipComponent: 'CustomTooltip',
      },
      flugAufgabenKategorien: [
        'Individualisierung',
        'Sonstiges',
        'Flugplanaenderung',
        'VorzeitigesTicketing',
        'SegmenteMismatch',
        'Flugbuchung',
        'InlandsflugPruefung',
        'Flugpruefung',
        'InlandsflugBuchung',
        'GruppeTicketing',
        'SpecialServices',
        'TicketfreigabeYield',
        'TicketingAnfrage',
        'FlugplanaenderungAbsprache',
        'InlandsflugAbsprache',
        'UnverbindlichesFlugangebot',
      ],
    };
  },
  created() {
    const centerIconStyle = {
      display: 'flex',
      'align-items': 'center',
      'justify-content': 'center',
    };
    this.columnDefs = [
      {
        checkboxSelection: true,
        headerCheckboxSelection: true,
        field: 'id',
        headerName: 'BNR',
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        cellRenderer: 'CellRendererBnrMitGrWunsch',
        width: 145,
      },
      {
        field: 'ueberschrift',
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        headerName: 'Reise',
        sortable: true,
        filterParams: {
          values: params => params.success(this.reisekuerzelOptions.map(r => r.reisekuerzel)),
          debounceMs: 1000,
        },
        width: 100,
      },
      {
        field: 'reisedatum',
        headerName: 'Abreise',
        cellRenderer: params => (params.value ? format(new Date(params.value), 'dd.MM.yyyy') : '-'),
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        width: 185,
      },
      {
        field: 'startdatum',
        headerName: 'Start\u00ADdatum',
        valueFormatter: params => {
          return params.data.hotelleistungen
            .map(item => format(new Date(item.reisetermin.startdatum), 'dd.MM.yyyy'))
            .sort((a, b) => (isAfter(new Date(b), new Date(a)) ? 1 : -1))[0];
        },
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        width: 185,
      },
      {
        field: 'istInKlaerung',
        headerName: 'Ein\u00ADschränkung',
        cellRenderer: params => {
          const istInKlaerung = params.data.hotelleistungen?.some(hl => hl.reisetermin.istInKlaerung == true);
          const isKritischeDestination = params.data.hotelleistungen?.some(
            hl => hl.reisetermin.reise?.isKritischeDestination == true
          );
          const icons = [];
          if (istInKlaerung) {
            icons.push('<i class="fas fa-exclamation-triangle text-warning fa-lg"></i>');
          }
          if (isKritischeDestination) {
            icons.push('<i class="fas fa-exclamation text-danger fa-lg"></i>');
          }
          return icons.length > 0 ? icons.join(' ') : '-';
        },
        cellStyle: centerIconStyle,
        width: 115,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['none', 'istInKlaerung', 'isKritischeDestination'],
          cellRenderer: params => {
            if (params.value === '(Select All)') return params.value;
            if (params.value === 'istInKlaerung')
              return `<div style="display: flex; align-items: center;">
                      <span style="width: 20px; display: inline-flex; justify-content: center;"><i class='fas fa-exclamation-triangle text-warning fa-lg'></i></span>
                      <span style="margin-left: 2px">In Klärung</span>
                      </div>`;

            if (params.value === 'isKritischeDestination')
              return `<div style="display: flex; align-items: center;">
                      <span style="width: 20px; display: inline-flex; justify-content: center;"><i class='fas fa-exclamation text-danger fa-lg'></i></span>
                      <span style="margin-left: 2px">Kritische Destination</span>
                      </div>`;

            return '-';
          },
          debounceMs: 1000,
        },
      },
      {
        field: 'releasedatum',
        headerName: 'Release\u00ADdatum',
        valueFormatter: params => {
          return params.data.hotelleistungen
            .map(item => format(new Date(item.reisetermin.releasedatum), 'dd.MM.yyyy'))
            .sort((a, b) => (isAfter(new Date(b), new Date(a)) ? 1 : -1))[0];
        },
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        width: 185,
      },
      {
        field: 'garantiert',
        headerName: 'Gar.',
        cellRenderer: params => {
          const value = params.data.hotelleistungen.sort((a, b) =>
            isAfter(
              new Date(format(new Date(b.reisetermin.startdatum), 'dd.MM.yyyy')),
              new Date(format(new Date(a.reisetermin.startdatum), 'dd.MM.yyyy'))
            )
          )[0]?.reisetermin.garantiert;
          return value
            ? '<i class="fas fa-check text-success fa-lg"></i>'
            : '<i class="fas fa-times text-danger fa-lg"></i>';
        },
        cellStyle: centerIconStyle,
        width: 70,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value
              ? "<i class='fas fa-check text-success fa-lg'/>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'/>",
          debounceMs: 1000,
        },
      },
      {
        field: 'getEarliestTktlFlugbuchung',
        headerName: 'TKTL bis',
        sortable: true,
        cellRenderer: params =>
          params.value == null ? '-' : format(parseISO(params.value.slice(0, -1)), 'dd.MM.yyyy HH:mm'),
        filter: 'AgDateRangePicker',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        width: 185,
      },
      {
        field: 'pnrs',
        headerName: 'PNR',
        filter: 'agTextColumnFilter',
        tooltipValueGetter: params =>
          params.value && params.value.length > 0 ? params.value.map(x => x.pnr).join(', ') : null,
        filterParams: { buttons: ['apply'] },
        cellRenderer: params =>
          params.value && params.value.length > 0 ? params.value.map(x => x.pnr).join(', ') : '-',
        width: 150,
      },
      {
        field: 'npx',
        headerName: 'PAX',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        filter: 'agTextColumnFilter',
        filterParams: { buttons: ['apply'] },
        width: 70,
      },
      {
        field: 'flugbuchungVorhanden',
        headerName: 'Flug\u00ADbuchung',
        cellRenderer: params =>
          params.value
            ? '<i class="fas fa-check text-success fa-lg"></i>'
            : '<i class="fas fa-times text-danger fa-lg"></i>',
        cellStyle: centerIconStyle,
        width: 90,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value
              ? "<i class='fas fa-check text-success fa-lg'/>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'/>",
          debounceMs: 1000,
        },
      },
      {
        field: 'statusPruefung',
        headerName: 'Status Prüfung',
        width: 85,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['AllGood', 'SomethingWrong', 'NoSegment'],
          debounceMs: 1000,
          cellRenderer: params =>
            params.value === '(Select All)' ? params.value : this.statusPruefungConverter[params.value],
        },
        cellRenderer: params => this.statusPruefungConverter[params.value],
        cellStyle: centerIconStyle,
      },

      {
        field: 'statusTickets',
        headerName: 'Status Tickets',
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['AllGood', 'CheckRequired', 'Critical'],
          debounceMs: 1000,
          cellRenderer: params =>
            params.value === '(Select All)' ? params.value : this.statusTicketsConverter[params.value],
        },
        cellRenderer: params =>
          this.statusTicketsConverter[params.value] || this.statusTicketsConverter.Critical,
        cellStyle: centerIconStyle,
      },
      {
        field: 'isTicketAusstellbar',
        headerName: 'Ticket ausstellbar',
        cellRenderer: params =>
          params.value
            ? '<i class="fas fa-check text-success fa-lg"></i>'
            : '<i class="fas fa-times text-danger fa-lg"></i>',
        tooltipComponent: 'BeschreibungTooltip',
        tooltipValueGetter: params => {
          const reasons = params.data?.reasonsTicketIsNotAusstellbarText;
          if (!params.value && reasons) {
            const fixUmlaute = umlaut =>
              umlaut
                .replace(/Ae/g, 'Ä')
                .replace(/Oe/g, 'Ö')
                .replace(/Ue/g, 'Ü')
                .replace(/ae/g, 'ä')
                .replace(/oe/g, 'ö')
                .replace(/ue/g, 'ü')
                .replace(/StatusPrüfung/g, 'Status Prüfung');
            const items = reasons
              .split(',')
              .map(r => fixUmlaute(r.trim()))
              .filter(Boolean);
            return (
              '<div style="font-size: 13px;">' +
              '<div style="font-weight: 600; margin-bottom: 6px;">Ticket nicht ausstellbar</div>' +
              items
                .map(
                  r =>
                    '<div style="display: flex; align-items: baseline; gap: 6px; margin-bottom: 2px;">' +
                    '<i class="fas fa-times text-danger" style="font-size: 10px;"></i>' +
                    `<span>${r}</span>` +
                    '</div>'
                )
                .join('') +
              '</div>'
            );
          }
          return null;
        },
        cellStyle: centerIconStyle,
        width: 100,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params =>
            params.value === '(Select All)'
              ? params.value
              : params.value
              ? "<i class='fas fa-check text-success fa-lg'/>"
              : "<i class='ml-1 fas fa-times text-danger fa-lg'/>",
          debounceMs: 1000,
        },
      },
      {
        field: 'ticketAngefragtStatus',
        headerName: '',
        headerTooltip: 'Ticket automatisch angefragt',
        headerComponentParams: {
          iconComponent: Bot,
          iconSize: 20,
          centered: true,
        },
        cellRenderer: 'CellRendererTicketBot',
        cellStyle: centerIconStyle,
        width: 60,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params => {
            if (params.value === '(Select All)') return params.value;
            return params.value
              ? '<span class="d-inline-flex align-items-center"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg></span>'
              : '-';
          },
          debounceMs: 1000,
        },
      },
      {
        field: 'akzeptiert',
        headerName: 'Akzeptiert',
        width: 140,
        filter: 'agSetColumnFilter',
        cellRenderer: 'CellRendererAkzeptiertFlugbuchungen',
        cellRendererParams: {
          onClickAkzeptiertStatusPruefung: this.onClickAkzeptiertStatusPruefung,
        },
        filterParams: {
          values: ['Akzeptiert', 'War akzeptiert', 'Nicht akzeptiert'],
          debounceMs: 1000,
        },
      },
      {
        field: 'aufgaben',
        headerName: 'Aufgaben',
        cellRenderer: 'CellRendererAufgaben',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: this.flugAufgabenKategorien,
          valueFormatter: params =>
            this.getAllKategorien?.find(item => item.aufgabenKategorie === params.value)?.labelFrontend,
          debounceMs: 1000,
        },
        cellRendererParams: {
          filter: 'only-flug-team',
          ansicht: 'vorgang-overview',
          kategorienToShow: this.flugAufgabenKategorien,
        },
        minWidth: 500,
        width: 500,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'align-items': 'center',
        },
        flex: 1,
        sortable: true,
      },
      {
        field: 'actions',
        headerName: 'Aktionen',
        pinned: 'right',
        cellRenderer: 'CellRendererAktionenFlugbuchungen',
        cellRendererParams: {
          onShowModalAufgabeEdit: this.onShowModalAufgabeEdit,
        },
        maxWidth: 110,
      },
    ];
  },
  computed: {
    ...mapState({
      reisekuerzelOptions: state => state.reisen.reisenkuerzelFilterOptions,
    }),
    ...mapGetters(['getAllKategorien', 'getFlugTeamUsersIds']),
    allVorgaengeWithSameReisetermin() {
      if (this.allSelectedRows.length < 2) return false;

      // speichere alle reisetermin-IDs in diesem Set
      const alleReiseterminIds = new Set();

      this.allSelectedRows.forEach(row => {
        const reisetermine = row.data.hotelleistungen
          .filter(hotelleistung => hotelleistung.isDeleted === false)
          .map(hotelleistung => hotelleistung.reisetermin?.id)
          .filter(id => id != null);

        reisetermine.forEach(id => alleReiseterminIds.add(id));
      });

      // wenn sie genau einen gemeinsamen reisetermin haben
      return alleReiseterminIds.size === 1;
    },
  },
  methods: {
    onPriorityClick($event) {
      const { id: priorityText } = $event.target;

      if (priorityText === 'Alle') {
        this.prioritiesSelected = [];
      } else if (!this.prioritiesSelected.includes(priorityText)) {
        this.prioritiesSelected.push(priorityText);
      } else {
        this.prioritiesSelected = this.prioritiesSelected.filter(priority => priority !== priorityText);
      }
      // Wenn alle angeklickt sind, resette
      if (this.prioritiesSelected.length === this.priorities.length - 1) {
        this.prioritiesSelected = [];
      }

      this.gridApi.onFilterChanged();
    },
    async onClickAkzeptiertStatusPruefung(vorgangId, kommentar, status, hideModal) {
      await apiService
        .post('Vorgang/change-status-pruefung', { id: vorgangId, kommentar, status })
        .then(() => {
          this.gridApi.forEachNode(rowNode => {
            if (rowNode.data.id === vorgangId) {
              rowNode.updateData({ ...rowNode.data, statusPruefungAkzeptiert: status });
            }
          });
          const params = {
            force: true,
            suppressFlash: false,
          };
          this.gridApi.refreshCells(params);
          this.toast(`Vorgang ${vorgangId} wurde akzeptiert.`);
        })
        .finally(() => {
          hideModal();
        });
    },
    selectRowOnCellClicked(params) {
      if (params.column.colId !== 'actions') {
        params.node.setSelected(!params.node.isSelected());
      }
    },
    onRowClicked(params) {
      // only master rows
      if (!params.node.master) return;

      const hasClickedAufgabenWidget = params.eventPath
        ?.map(a => a.className)
        .some(a => a?.includes('hoverAufgabenWidget'));
      // if I clicked on any <button> (or child of one), don’t expand
      if (
        params.event.target.closest('button') ||
        params.event.target.closest('a') ||
        hasClickedAufgabenWidget
      ) {
        return;
      }

      this.gridApi.setRowNodeExpanded(params.node, !params.node.expanded);
    },
    resetAllFilters() {
      this.gridApi.setFilterModel(null);

      const istInKlaerungFilter = this.gridApi.getFilterInstance('istInKlaerung');
      istInKlaerungFilter.setModel({
        values: ['none'],
      });

      this.prioritiesSelected = [];
      this.erledigteVorgaengeAusblenden = true;
      this.akzeptierteVorgaengeAusblenden = true;

      const query = { ...this.$route.query };
      if ('id' in query) {
        delete query['id'];
        this.$router.push({ query });
      }

      let filterDate1 = this.gridApi.getFilterInstance('reisedatum');
      let filterDate2 = this.gridApi.getFilterInstance('startdatum');
      let filterDate3 = this.gridApi.getFilterInstance('getEarliestTktlFlugbuchung');
      let filterDate4 = this.gridApi.getFilterInstance('releasedatum');
      filterDate1.onDateRangeFilterChanged(null);
      filterDate2.onDateRangeFilterChanged(null);
      filterDate3.onDateRangeFilterChanged(null);
      filterDate4.onDateRangeFilterChanged(null);
    },
    checkIfFilterSupplied() {
      const usedFilters = Object.keys(this.gridApi?.getFilterModel()).filter(
        item => item !== 'istInKlaerung'
      );

      const istInKlaerungValues = this.gridApi.getFilterInstance('istInKlaerung')?.appliedModel?.values;
      const defaultIstInKlaerungValuesDiffer = !(
        istInKlaerungValues?.length === 1 && istInKlaerungValues?.includes('none')
      );

      this.isFilterSupplied =
        usedFilters.length > 0 ||
        !this.erledigteVorgaengeAusblenden ||
        !this.akzeptierteVorgaengeAusblenden ||
        this.prioritiesSelected.length > 0 ||
        defaultIstInKlaerungValuesDiffer;
    },
    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      const istInKlaerungFilter = this.gridApi.getFilterInstance('istInKlaerung');
      istInKlaerungFilter.setModel({
        values: ['none'],
      });

      this.gridApi.onFilterChanged();

      if (this.$route.query.id) {
        const idFilter = this.gridApi?.getFilterInstance('id');
        if (!isNaN(this.$route.query.id))
          idFilter.setModel({ filterType: 'text', filter: this.$route.query.id, type: 'contains' });
        this.gridApi.onFilterChanged();
      }

      const updateData = () => {
        const server = this.server();
        const datasource = this.createDatasource(server);
        params.api.setServerSideDatasource(datasource);
      };

      updateData();
    },
    createDatasource(server) {
      return {
        getRows: async params => {
          console.log('[Datasource] - rows requested by grid: ', params.request);
          const response = await server.getData(params.request);

          if (response.success) {
            params.success({
              rowData: response.rows,
            });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());

            if (this.$route.query.id && !isNaN(this.$route.query.id) && response.rows.length === 1) {
              this.gridApi.forEachNode(rowNode => {
                if (+rowNode.data.id === +this.$route.query.id) {
                  this.gridApi.setRowNodeExpanded(rowNode, !rowNode.expanded);
                }
              });
            }
          } else {
            params.fail();
          }
        },
      };
    },
    server() {
      return {
        getData: async request => {
          this.checkIfFilterSupplied();

          console.log('[Datasource] - rows requested by grid: ', request);
          const agGridFilters = this.getFilters(request);
          const orderBy =
            request.sortModel.length > 0
              ? request.sortModel.map(
                  s =>
                    s.colId
                      .replaceAll('.', '/')
                      .replace('aufgaben', 'getEarliestFaelligkeitFomAufgaben')
                      .replace('releasedatum', 'fruehestesReleasedatum')
                      .replace('startdatum', 'fruehestesStartdatum') +
                    ' ' +
                    s.sort
                )
              : 'reisedatum asc';

          const response = await odataService.getVorgang({
            filter: agGridFilters,
            count: true,
            select: [
              'id',
              'ueberschrift',
              'reisedatum',
              'getEarliestTktlFlugbuchung',
              'npx',
              'flugbuchungVorhanden',
              'statusPruefung',
              'statusTickets',
              'isTicketAusstellbar',
              'reasonsTicketIsNotAusstellbarText',
              'statusPruefungAkzeptiert',
              'WarStatusPruefungAkzeptiert',
              'ticketAngefragtStatus',
            ],
            top: request.endRow - request.startRow,
            skip: request.startRow,
            expand: {
              pnrs: {},
              hotelleistungen: {
                select: ['id', 'thMainBuchung', 'thAlleAddonsBuchung', 'thGesamtBuchung', 'isDeleted'],
                filter: { isDeleted: false },
                expand: {
                  reisetermin: {
                    select: [
                      'id',
                      'reiseterminkuerzel',
                      'garantiert',
                      'startdatum',
                      'releasedatum',
                      'istInKlaerung',
                    ],
                    expand: {
                      reise: {
                        select: ['isKritischeDestination'],
                      },
                    },
                  },
                },
              },
              aufgaben: { filter: { status: { ne: 'Erledigt' } } },
              teilnehmers: { select: ['kundenwuensche'], filter: { deletedAt: null } },
            },
            orderBy,
          });

          this.count = response.count;
          return {
            success: true,
            rows: response.data,
          };
        },
      };
    },
    onReload() {
      this.gridApi.onFilterChanged();
    },
    getRowHeight(params) {
      const amountFlugAufgaben = params.data?.aufgaben?.filter(
        aufgabe =>
          this.flugAufgabenKategorien.includes(aufgabe.kategorie) &&
          (aufgabe.team === 'Flight Operations Management' ||
            this.getFlugTeamUsersIds.includes(aufgabe.bearbeiter))
      ).length;
      // Akzeptiert-Spalte: Status-Button + ggf. "War akzeptiert"-Badge je in einer Zeile
      const amountAkzeptiertZeilen = 1 + (params.data?.warStatusPruefungAkzeptiert ? 1 : 0);
      return params.data ? Math.max(1, amountFlugAufgaben, amountAkzeptiertZeilen) * 45 : 45;
    },
    getFilters(request) {
      if (request.filterModel == null) return {};
      const filterKeys = Object.keys(request.filterModel);

      let filters = filterKeys.map(key => {
        const formattedKey = key.replaceAll('.', '/');

        if (request.filterModel[key].filterType === 'text') {
          if (['npx'].includes(formattedKey)) {
            if (request.filterModel[key].filter === '-') {
              return { [formattedKey]: null };
            } else if (request.filterModel[key].filter.includes('-')) {
              const min = parseInt(request.filterModel[key].filter.split('-')[0]);
              const max = parseInt(request.filterModel[key].filter.split('-')[1]);
              return { [formattedKey]: { le: max, ge: min } };
            } else if (request.filterModel[key].filter[0] === '>') {
              const min = parseInt(request.filterModel[key].filter.split('>')[1]);
              return { [formattedKey]: { gt: min } };
            } else if (request.filterModel[key].filter[0] === '<') {
              const max = parseInt(request.filterModel[key].filter.split('<')[1]);
              return { [formattedKey]: { lt: max } };
            } else return { [formattedKey]: parseInt(request.filterModel[key].filter) };
          }
          if (['thMainBuchung', 'thAlleAddonsBuchung', 'thGesamtBuchung'].includes(formattedKey)) {
            let condition;

            if (request.filterModel[key].filter === '-') {
              condition = { [formattedKey]: null };
            } else if (request.filterModel[key].filter.includes('-')) {
              const min = parseInt(request.filterModel[key].filter.split('-')[0]);
              const max = parseInt(request.filterModel[key].filter.split('-')[1]);
              condition = { [formattedKey]: { le: max, ge: min } };
            } else if (request.filterModel[key].filter[0] === '>') {
              const min = parseInt(request.filterModel[key].filter.split('>')[1]);
              condition = { [formattedKey]: { gt: min } };
            } else if (request.filterModel[key].filter[0] === '<') {
              const max = parseInt(request.filterModel[key].filter.split('<')[1]);
              condition = { [formattedKey]: { lt: max } };
            } else {
              condition = { [formattedKey]: parseInt(request.filterModel[key].filter) };
            }

            return {
              hotelleistungen: {
                any: condition,
              },
            };
          }
          if (formattedKey === 'id') {
            if (isNaN(request.filterModel[key].filter)) return;
            return { [formattedKey]: { eq: +request.filterModel[key].filter } };
          }
          if (formattedKey === 'pnrs') {
            return {
              ['Pnrs']: {
                any: {
                  pnr: request.filterModel[key].filter,
                },
              },
            };
          }

          return { [formattedKey]: { contains: request.filterModel[key].filter } };
        } else if (request.filterModel[key].filterType === 'date') {
          const { dateFrom, dateTo } = request.filterModel[key];
          const utcAbreisedatum = new Date(
            Date.UTC(+dateFrom.substring(0, 4), +dateFrom.substring(5, 7) - 1, +dateFrom.substring(8, 10))
          );
          const utcEnddatum = add(
            new Date(
              Date.UTC(+dateTo.substring(0, 4), +dateTo.substring(5, 7) - 1, +dateTo.substring(8, 10))
            ),
            { hours: 23, minutes: 59, seconds: 59 }
          );

          if (formattedKey === 'startdatum' || formattedKey === 'releasedatum') {
            return {
              hotelleistungen: {
                any: {
                  isDeleted: false,
                  ['reisetermin/' + formattedKey]: {
                    ge: utcAbreisedatum,
                    le: utcEnddatum,
                  },
                },
              },
            };
          }

          if (formattedKey === 'getEarliestTktlFlugbuchung') {
            {
              return {
                flugbuchungen: {
                  any: {
                    ticketingTimelimitHauptstrecke: {
                      ge: utcAbreisedatum,
                      le: utcEnddatum,
                    },
                  },
                },
              };
            }
          }

          return {
            [formattedKey]: {
              ge: utcAbreisedatum,
              le: utcEnddatum,
            },
          };
        } else if (request.filterModel[key].filterType === 'set') {
          if (key === 'ticketAngefragtStatus') {
            const ticketAngefragtStatusConverter = {
              true: "ticketAngefragtStatus eq 'TicketAutomatischAngefragt'",
              false: "ticketAngefragtStatus ne 'TicketAutomatischAngefragt'",
            };
            return (
              '(' +
              request.filterModel[key].values
                .map(value => ticketAngefragtStatusConverter[value])
                .join(' or ') +
              ')'
            );
          }

          if (key === 'flugbuchungVorhanden') {
            const odataFlugbuchunVorhandenConverter = {
              true: 'flugbuchungen/any() eq true',
              false: 'flugbuchungen/any() eq false',
            };
            return (
              '(' +
              request.filterModel[key].values
                .map(value => odataFlugbuchunVorhandenConverter[value])
                .join(' or ') +
              ')'
            );
          }

          if (key === 'garantiert') {
            return {
              hotelleistungen: {
                any: {
                  isDeleted: false,
                  ['reisetermin/garantiert']: { in: request.filterModel[key].values },
                },
              },
            };
          }
          if (key === 'istInKlaerung') {
            const filterValues = request.filterModel[key].values;
            const conditions = [];

            if (filterValues.includes('istInKlaerung')) {
              conditions.push({
                hotelleistungen: {
                  any: {
                    isDeleted: false,
                    ['reisetermin/istInKlaerung']: true,
                  },
                },
              });
            }
            if (filterValues.includes('isKritischeDestination')) {
              conditions.push({
                hotelleistungen: {
                  any: {
                    isDeleted: false,
                    ['reisetermin/reise/isKritischeDestination']: true,
                  },
                },
              });
            }
            if (filterValues.includes('none')) {
              conditions.push({
                and: [
                  {
                    hotelleistungen: {
                      all: {
                        or: [{ isDeleted: true }, { ['reisetermin/istInKlaerung']: false }],
                      },
                    },
                  },
                  {
                    hotelleistungen: {
                      all: {
                        or: [{ isDeleted: true }, { ['reisetermin/reise/isKritischeDestination']: false }],
                      },
                    },
                  },
                ],
              });
            }

            if (conditions.length === 1) {
              return conditions[0];
            }
            return { or: conditions };
          }
          if (key === 'akzeptiert') {
            const akzeptiertFilterConverter = {
              Akzeptiert: 'statusPruefungAkzeptiert eq true',
              'Nicht akzeptiert': 'statusPruefungAkzeptiert eq false',
              'War akzeptiert': 'warStatusPruefungAkzeptiert eq true',
            };
            this.akzeptierteVorgaengeAusblenden = false;

            return (
              '(' +
              request.filterModel[key].values.map(value => akzeptiertFilterConverter[value]).join(' or ') +
              ')'
            );
          }

          if (key === 'isTicketAusstellbar') {
            this.akzeptierteVorgaengeAusblenden = false;
          }

          if (key === 'aufgaben') {
            return {
              aufgaben: {
                any: {
                  kategorie: { in: request.filterModel[key].values },
                  status: { ne: 'Erledigt' },
                  or: [
                    { team: 'Flight Operations Management' },
                    { bearbeiter: { in: this.getFlugTeamUsersIds } },
                  ],
                },
              },
            };
          }
          return {
            [formattedKey]: { in: request.filterModel[key].values },
          };
        }
      });
      filters = [
        ...filters,
        {
          and: [
            {
              or: [
                { anreiseart: 'Flug' },
                {
                  anreiseart: 'KeinFlug',
                  aufgaben: { any: { kategorie: 'InlandsflugBuchung', status: { ne: 'Erledigt' } } },
                },
              ],
            },
            {
              or: [
                { flugbuchungVorhanden: true },
                {
                  aufgaben: {
                    any: {
                      kategorie: { in: this.flugAufgabenKategorien },
                      status: { ne: 'Erledigt' },
                    },
                  },
                },
              ],
            },
          ],
          enddatum: { ge: new Date() },
          isDeleted: false,
          blankState: 'Buchung',
          hotelleistungen: {
            any: {
              reisetermin: {},
            },
          },
        },
      ];

      if (this.prioritiesSelected.length > 0) {
        filters = [
          ...filters,
          {
            hotelleistungen: {
              any: {
                ['reisetermin/reise/prioritaet']: {
                  in: this.prioritiesSelected.map(
                    prioritaet => this.priorities.find(p => p.text === prioritaet)?.value
                  ),
                },
              },
            },
          },
        ];
      }

      if (filterKeys.includes('statusPruefung') || filterKeys.includes('statusTickets')) {
        this.erledigteVorgaengeAusblenden = false;
      }

      if (this.erledigteVorgaengeAusblenden) {
        filters = [
          ...filters,
          {
            or: [
              { statusPruefung: { ne: 'AllGood' } },
              { statusTickets: { ne: 'AllGood' } },
              {
                aufgaben: {
                  any: {
                    kategorie: { in: this.flugAufgabenKategorien },
                    status: { ne: 'Erledigt' },
                    or: [
                      { team: 'Flight Operations Management' },
                      { bearbeiter: { in: this.getFlugTeamUsersIds } },
                    ],
                  },
                },
              },
            ],
          },
        ];
      }

      if (this.akzeptierteVorgaengeAusblenden) {
        filters = [
          ...filters,
          {
            or: [
              { statusPruefungAkzeptiert: false },
              {
                aufgaben: {
                  any: {
                    kategorie: { in: this.flugAufgabenKategorien },
                    status: { ne: 'Erledigt' },
                  },
                },
              },
            ],
          },
        ];
      }

      return filters;
    },
    onErledigteVorgaengeAusblendenClick() {
      this.gridApi.onFilterChanged();
    },
    resetFilters() {
      this.gridApi.onFilterChanged();
    },
    onRowSelected() {
      this.allSelectedRows = this.getAllSelectedRows();
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setMarkedCount(this.allSelectedRows.length);
    },
    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi.forEachNode((node, index) => {
        if (node.selected) {
          rowsSelected.push(node);
        }
      });
      return rowsSelected;
    },
    onShowModalAufgabeEdit(prefixedVerlinkungen) {
      this.prefixedVerlinkungen = prefixedVerlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },
    openAufgabenModalWithData() {
      let idCounter = 0;
      const verlinkungen = [];

      // Füge alle ausgewählten Vorgänge hinzu
      this.allSelectedRows.forEach(row => {
        verlinkungen.push({
          id: idCounter++,
          typ: 'Vorgang',
          artifactId: row.data.id,
        });
      });

      // Finde und füge den gemeinsamen Reisetermin hinzu
      if (this.allSelectedRows.length > 0) {
        const reisetermin = this.allSelectedRows[0].data.hotelleistungen.find(
          hotelleistung => hotelleistung.isDeleted === false
        )?.reisetermin;

        if (reisetermin) {
          verlinkungen.push({
            id: idCounter++,
            typ: 'Reisetermin',
            artifactId: reisetermin.id,
            artifactText: reisetermin.reiseterminkuerzel,
          });
        }
      }

      this.prefixedVerlinkungen = verlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },
  },
};
</script>
<style lang="scss" scoped>
:deep(.ag-row-group-expanded) {
  background-color: #e5f0fb;
}

// Header-Text über zwei Zeilen umbrechen, damit genug Platz ist
:deep(.ag-header-cell-comp-wrapper) {
  width: 100%;
}

// Umbruch nur an manuell gesetzten Trennstellen (weiches Trennzeichen )
:deep(.ag-header-cell-comp-wrapper .mr-2) {
  white-space: normal;
  hyphens: manual;
  -webkit-hyphens: manual;
  overflow-wrap: break-word;
  min-width: 0;
  line-height: 1.15;
}

// Header-Icon (z.B. Bot) ohne rechten Rand, damit es mittig sitzt
:deep(.ag-header-cell-comp-wrapper svg.mr-2) {
  margin-right: 0 !important;
}

.icon-button {
  height: 32.52px;
}
</style>
